import React from 'react';

/**
 * StatCard - Componente reutilizavel para cards de estatisticas
 *
 * Uso:
 * <StatCard
 *   title="Total"
 *   value={100}
 *   icon={<Users size={24} />}
 *   color="var(--brand, #20a490)"
 *   trend={{ value: 10, positive: true }}
 * />
 */

const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  color = 'var(--brand, #20a490)',
  trend,
  onClick,
  size = 'normal' // 'small', 'normal', 'large'
}) => {
  const formatValue = (val) => {
    if (typeof val === 'number') {
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
      return val.toLocaleString('pt-BR');
    }
    return val;
  };

  const sizeStyles = {
    small: { padding: '16px', minWidth: '140px' },
    normal: { padding: '20px', minWidth: '180px' },
    large: { padding: '24px', minWidth: '220px' },
  };

  return (
    <div
      className="stat-card"
      style={{
        ...styles.card,
        ...sizeStyles[size],
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      {/* Icone */}
      {icon && (
        <div style={{ ...styles.iconContainer, backgroundColor: `${color}15` }}>
          <div style={{ ...styles.icon, color }}>{icon}</div>
        </div>
      )}

      {/* Conteudo */}
      <div style={styles.content}>
        <span style={styles.title}>{title}</span>
        <div style={styles.valueRow}>
          <span style={{ ...styles.value, color }}>{formatValue(value)}</span>
          {trend && (
            <span style={{
              ...styles.trend,
              color: trend.positive ? '#27ae60' : '#e74c3c'
            }}>
              {trend.positive ? '↑' : '↓'} {Math.abs(trend.value)}%
            </span>
          )}
        </div>
        {subtitle && <span style={styles.subtitle}>{subtitle}</span>}
      </div>

      {/* Barra de acento */}
      <div style={{ ...styles.accentBar, backgroundColor: color }} />
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  title: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
  },
  value: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: 1,
  },
  trend: {
    fontSize: '12px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#999',
    marginTop: '4px',
  },
  accentBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
  },
};

export default StatCard;
